import { Bot, ShoppingCart } from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { leadStatus } from '@/theme';
import type { ConversacionResumen } from '@/types/conversations';
import {
  botStateMeta,
  channelMeta,
  contactName,
  describeConversationStats,
  describeCount,
  describeLastMessage,
  describeOrdenResumen,
  formatRelativeTime,
} from './inbox-format';

/**
 * Una fila de la bandeja: quién es, por dónde escribe, cómo está el lead, quién contesta y qué fue
 * lo último que pasó.
 *
 * Es un `<button>` (no un `<div>` clickeable) para que se pueda abrir con Enter/Espacio y para que
 * el lector de pantalla lo anuncie como acción; por eso todo el interior son `<span>` con
 * `block`/`flex`, ya que un `<button>` no admite elementos de bloque como hijos válidos.
 *
 * Los datos que NO se repiten acá (historial, pedidos, teléfono completo) están en el panel de la
 * derecha: la bandeja tiene que dejar comparar conversaciones de un vistazo, no leerse entera.
 */

export interface ConversationItemProps {
  conversacion: ConversacionResumen;
  selected: boolean;
  /** ¿Tiene un mensaje del cliente que el usuario todavía no ha visto? (punto azul) */
  nueva?: boolean;
  onSelect: (conversationId: string) => void;
}

export function ConversationItem({
  conversacion,
  selected,
  nueva = false,
  onSelect,
}: ConversationItemProps) {
  const nombre = contactName(conversacion);
  const canal = channelMeta[conversacion.channel];
  const CanalIcon = canal.icon;
  const lead = leadStatus[conversacion.estadoLead];
  const bot = botStateMeta(conversacion.botActivo);
  const ultimo = describeLastMessage(conversacion);

  return (
    <li>
      <button
        type="button"
        aria-current={selected ? 'true' : undefined}
        aria-label={`Abrir la conversación de ${nombre}. ${ultimo}${
          nueva ? ' Mensajes nuevos sin ver.' : ''
        }`}
        onClick={() => onSelect(conversacion.id)}
        className={cn(
          'w-full rounded-panel border p-3 text-left transition-colors',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
          selected
            ? 'border-brand-500 bg-brand-50'
            : 'border-transparent hover:bg-surface-muted',
        )}
      >
        <span className="flex items-center gap-3">
          <Avatar name={nombre} size="md" />

          <span className="min-w-0 flex-1">
            <span className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-1.5">
                {/* Punto de «sin ver»: la fila con un mensaje del cliente todavía sin abrir. Se
                    apaga solo al abrir la conversación (ver `useInboxAlerts`). */}
                {nueva ? (
                  <span
                    aria-hidden="true"
                    className="size-2 shrink-0 rounded-pill bg-brand-600"
                  />
                ) : null}
                <span className="truncate text-body font-semibold text-ink">{nombre}</span>
              </span>
              <span className="shrink-0 text-micro text-ink-faint">
                {formatRelativeTime(conversacion.lastMessageAt)}
              </span>
            </span>

            <span className="mt-0.5 block truncate text-caption text-ink-muted">{ultimo}</span>
          </span>
        </span>

        <span className="mt-2 flex flex-wrap items-center gap-1.5">
          <Badge tone={canal.tone} icon={<CanalIcon size={12} />}>
            {canal.label}
          </Badge>

          <StatusBadge label={lead.label} tone={lead.tone} />

          {/* El estado del bot es la información operativa de la fila: si está pausado, esta
              conversación la tiene que contestar una persona. */}
          <Badge tone={bot.tone} icon={<Bot size={12} />}>
            {bot.label}
          </Badge>

          {conversacion.ordenesTotal > 0 ? (
            <Badge tone="success" icon={<ShoppingCart size={12} />}>
              {describeCount(conversacion.ordenesTotal, 'pedido', 'pedidos')}
            </Badge>
          ) : null}
        </span>

        <span className="mt-2 block text-micro text-ink-muted">
          {describeConversationStats(conversacion)}
          {conversacion.ultimaOrden
            ? ` · último: ${describeOrdenResumen(conversacion.ultimaOrden)}`
            : ''}
        </span>
      </button>
    </li>
  );
}
